import dayjs from 'dayjs';
import { Button, Form } from 'antd';
// import CustomAccordion from '../CustomAccordion';
import pdfIcon from 'assets/images/icons/pdf_red.svg';
import pdfIconDisable from 'assets/images/icons/pdf_white.svg';
import CardAccordion from 'components/CardAccordion/CardAccordion';
import { Box, Chip, Table, TableRow, TableBody, TableCell, TableHead, Typography, TableContainer } from '@mui/material';

const getOrdinalSuffix = (num) => {
	const j = num % 10;
	const k = num % 100;
	if (j === 1 && k !== 11) {
		return num + 'st';
	}
	if (j === 2 && k !== 12) {
		return num + 'nd';
	}
	if (j === 3 && k !== 13) {
		return num + 'rd';
	}
	return num + 'th';
};

const GrossRentHistory = ({ data = [], index, otherChanges }) => {
	const form = Form.useFormInstance();
	const effectiveDateHistory = form.getFieldValue('effectiveDateHistory');

	return (
		<>
			<Box
				sx={{
					display: 'flex',
					alignItems: 'center',
					gap: 1.5, // Reduced gap
					borderBottom: '1px solid #f0f0f0',
					marginBottom: '10px',
					marginTop: index === 0 ? '0px' : '10px',
					flexWrap: 'wrap',
					minHeight: '40px',
				}}
			>
				{/* Main title with icon */}
				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
						gap: '8px',
						flexShrink: 0,
					}}
				>
					<Box
						sx={{
							width: '24px',
							height: '24px',
							borderRadius: '50%',
							backgroundColor: index === 0 ? '#4caf50' : '#ff9800',
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							color: 'white',
							fontSize: '12px',
							fontWeight: 'bold',
						}}
					>
						{index === 0 ? '1' : index + 1}
					</Box>
					<Typography
						sx={{
							fontSize: '13px',
							fontWeight: 'bold',
							whiteSpace: 'nowrap',
						}}
					>
						{index === 0 ? 'Initial - on creation:' : `${getOrdinalSuffix(index)} Modification:`}
					</Typography>
				</Box>

				{/* Date badge */}
				<Chip
					label={`Effective Date: ${dayjs(effectiveDateHistory?.[index]).format('DD-MM-YYYY')}`}
					variant="outlined"
					size="small"
					sx={{
						fontSize: '11px',
						height: '24px',
						backgroundColor: '#f8f9fa',
						borderColor: '#dee2e6',
						flexShrink: 0,
					}}
				/>

				{/* Info tags */}
				<Box
					sx={{
						display: 'flex',
						gap: '6px', // Reduced gap between chips
						flexWrap: 'wrap',
					}}
				>
					<Chip
						label={`IBR: ${otherChanges?.inputChangeDiscountRate}`}
						size="small"
						sx={{
							fontSize: '11px',
							backgroundColor: '#e8f5e8',
							height: '24px',
						}}
					/>
					<Chip
						label={`Payment Frequency: ${otherChanges?.paymentFrequency}`}
						size="small"
						sx={{
							fontSize: '11px',
							backgroundColor: '#fff3e0',
							height: '24px',
						}}
					/>
					<Chip
						label={`Payment Modality: ${otherChanges?.paymentTime?.toLowerCase() === 'start' ? 'Advance Basis' : 'Accrual Basis'}`}
						size="small"
						sx={{
							fontSize: '11px',
							backgroundColor: '#e3f2fd',
							height: '24px',
						}}
					/>
				</Box>
			</Box>

			<TableContainer
				className="advance-amount-table"
				sx={{
					'& table, & th, & td': {
						border: '1px solid #e0e0e0',
						borderCollapse: 'collapse',
					},
					'& th, & td': {
						p: '5px',
					},
					'& td .ant-form-item': {
						marginBottom: '0px',
					},

					'& th:nth-of-type(1), & td:nth-of-type(1)': { width: '20%' } /* From/From Month */,
					'& th:nth-of-type(2), & td:nth-of-type(2)': { width: '20%' } /* To/To Month */,
					'& th:nth-of-type(3), & td:nth-of-type(3)': { width: '20%' } /* Amount/Monthly Adjustment */,
					'& th:nth-of-type(4), & td:nth-of-type(4)': { width: '20%' } /* Remarks/Total Amount */,
					'& th:nth-of-type(5), & td:nth-of-type(5)': { width: '20%' } /* Action */,
					'& table': { width: '100%' } /* Ensure table takes full container width */,
				}}
			>
				<Table aria-label="Advance Amount table">
					<TableHead>
						<TableRow>
							<TableCell sx={{ textAlign: 'center' }}>From</TableCell>
							<TableCell sx={{ textAlign: 'center' }}>To</TableCell>
							<TableCell sx={{ textAlign: 'center' }}>Amount</TableCell>

							<TableCell sx={{ textAlign: 'center' }}>Notes</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{data.length > 0 ? (
							data.map((row, index) => {
								return (
									<TableRow key={index}>
										<TableCell style={{ textAlign: 'center' }}>{row.from}</TableCell>
										<TableCell style={{ textAlign: 'center' }}>{row.to}</TableCell>
										<TableCell style={{ textAlign: 'right' }}>{row.amount}</TableCell>

										<TableCell>{row.remarks || ''}</TableCell>
									</TableRow>
								);
							})
						) : (
							<TableRow>
								<TableCell colSpan={5} style={{ textAlign: 'center' }}>
									No data available
								</TableCell>
							</TableRow>
						)}
					</TableBody>
				</Table>
			</TableContainer>
		</>
	);
};

const LeaseV9NewTrailModeHistory = ({ viewData, selectHistory }) => {
	const form = Form.useFormInstance();
	const grossRentListSet = form.getFieldValue('grossRentListSet');

	return (
		<>
			<CardAccordion
				sx={{ mb: 2 }}
				darkTitle={true}
				title="Lease Payment Schedule"
				typographySX={{ fontSize: '16px !important' }}
				customHeaderSX={{
					p: '10px',
					backgroundColor: '#e1f0ff !important',
				}}
			>
				{grossRentListSet?.map((item, index) => (
					<GrossRentHistory key={index} data={item} index={index} otherChanges={viewData?.data?.changes[index]} />
				))}
			</CardAccordion>

			<CardAccordion
				title="Other Changes History"
				customHeaderSX={{
					backgroundColor: '#e1f0ff !important',
					p: '10px',
				}}
				typographySX={{ fontSize: '16px !important' }}
				darkTitle={true}
				sx={{ mb: 2 }}
			>
				<Button
					type="primary"
					style={{
						fontSize: '13px',
						fontWeight: '500',
						borderRadius: '6px',
						marginRight: '3px',
						display: 'flex',
						alignItems: 'center',
						gap: '6px',
						...(viewData?.history?.length === 0 ? { backgroundColor: '#6c757d' } : {}),
					}}
					onClick={(e) => {
						e.stopPropagation();
						if (selectHistory) {
							selectHistory({ ...viewData, ...viewData?.data, id: viewData?._id });
						} else if (viewData?.selectHistory) {
							viewData.selectHistory({ ...viewData, ...viewData?.data, id: viewData?._id });
						}
					}}
				>
					<img src={viewData?.history?.length > 1 ? pdfIcon : pdfIconDisable} width={16} height={16} alt="view" />
					Log
				</Button>
			</CardAccordion>
		</>
	);
};

export default LeaseV9NewTrailModeHistory;
